import React from 'react'
import { FaTimes } from 'react-icons/fa'
import classNames from 'classnames'
import { useQuery, useMutation } from 'jsonapi-react'
import { useLocation } from 'react-use'
import { useHistory } from 'react-router-dom'
import _ from 'lodash'

import Button from 'components/Button'
import AlbumItemThumb from 'components/Albums/Item/AlbumItemThumb'
import Modal, { Content } from 'components/Modal'
import Input, { InputCheckbox } from 'components/Forms/Input'
import Textarea from 'components/Forms/Textarea'
import LayoutPlayer from './layoutPlayer'
import KeyEvent from 'scenes/compositions/keyEvent'
import Loader from 'components/Loader'
import { Authenticated } from 'routes'


const letters = [
  'Q', 'W', 'E', 'R', 'T', 'Y', 'U',
  'I', 'O', 'P', 'A', 'S', 'D', 'F',
  'G', 'H', 'J', 'K', 'L', 'Z', 'X',
  'C', 'V', 'B', 'N', 'M',
]
export default function New(props) {
  const history = useHistory()
  const location = useLocation()
  const querySearch = new URLSearchParams(location.search)
  const albumIdSelected = querySearch.get('albumId')

  const requestRef = React.useRef()
  const previousTimeRef = React.useRef()

  const [rec, setRec] = React.useState(null)
  const [actions, setActions] = React.useState({
    audios: [],
    files: [],
  })
  const [items, setItems] = React.useState([])
  const [, updateState] = React.useState()
  const startRec = React.useRef(false)
  const recRef = React.useRef(false)

  const totalDuration = React.useRef(0)
  const beats = React.useRef([])
  const time = React.useRef(0)
  const keys = React.useRef([])
  const forceUpdate = React.useCallback(() => updateState({}), [])

  const animate = _time => {
    if (
      previousTimeRef.current !== undefined &&
      startRec.current
    ) {
      const deltaTime = _time - previousTimeRef.current
      time.current = time.current + deltaTime
      forceUpdate()
    }
    previousTimeRef.current = _time
    requestRef.current = requestAnimationFrame(animate)
  }

  const { data: album = {} } = useQuery([
    'albums',
    albumIdSelected,
    {
      include: 'items.file,items.audio,created_by',
    },
  ])

  const fetchItemAsync = async (item) => {
    let fileResponse = null
    let audioResponse = null
    let fileBlob = null
    let audioBlob = null
    let files = {
      ...item,
    }

    if (item.file?.file) {
      try {
        const fileToLoad = (
          item.file.type === 'Image' ?
          (item.file.large || item.file.file) :
          (item.file.medium || item.file.file)
        )
        fileResponse = await fetch(
          new Request(fileToLoad),
        )
        fileBlob = await fileResponse.blob()
        files = {
          file: {
            ...item.file,
            error:false,
            attempts: item.file?.attempts ? item.file.attempts + 1 : 0,
            blob: window.URL.createObjectURL(fileBlob)
          }
        }
      } catch (e) {
        if (item.file?.attempts < 3) {
          fetchItemAsync(item)
          return
        }
        files = {
          ...files,
          file: {
            ...files.file,
            error: true,
          }
        }
      }
    }

    if (item.audio?.file) {
      try {
        audioResponse = await fetch(
          new Request(item.audio?.file),
        )
        audioBlob = await audioResponse.blob()
        files = {
          ...files,
          audio: {
            ...item.audio,
            error: false,
            attempts: item.audio?.attempts ? item.audio.attempts + 1 : 0,
            blob: window.URL.createObjectURL(audioBlob)
          }
        }
      } catch (e) {
        if (item.audio?.attempts < 3) {
          fetchItemAsync(item)
          return
        }
        files = {
          ...files,
          audio: {
            ...files.audio,
            error: true,
          }
        }
      }
    }

    setItems(_items => _.map(
      _items,
      _item => {
        if (_item.id === item.id) {
          return {
            ...item,
            ...files,
          }
        }
        return _item
      }
    ))
  }

  const albumId = album.id
  React.useEffect(() => {
    if (albumId) {
      _.each(
        album.items,
        fetchItemAsync
      )
      setItems(album.items)
    }
  }, [albumId]) // eslint-disable-line

  const [ addComposition, { isLoading: isAddingComposition } ] = useMutation('compositions')

  React.useEffect(() => {
    if (rec) {
      setActions({
        audios: [],
        files: [],
      })

      requestRef.current = requestAnimationFrame(animate)
      recRef.current = true

    } else {
      cancelAnimationFrame(requestRef.current)

      const currentTime = _.parseInt(time.current, 10)

      totalDuration.current = currentTime

      setActions({
        audios: [],
        files: [],
      })

      beats.current = _.map(
        beats.current,
        beat => {
          if (! beat?.end_time) {
            return {
              ...beat,
              end_time: currentTime,
            }
          }
          return beat
        }
      )
    }
    return () => requestRef.current && cancelAnimationFrame(requestRef.current)

  }, [rec]) // eslint-disable-line

  const addBeat = React.useCallback(
    (file, currentTime) => {
      if (!file || currentTime === null) return
      beats.current.push({
        file: {
          id: file.id,
          type: 'File',
        },
        start_time: currentTime,
      })
    },
    [],
  )

  const removeBeat = React.useCallback(
    (file, currentTime) => {
      if (!file || currentTime === null) return
      beats.current = _.map(
        beats.current,
        beat => {
          if (beat.file && beat.file.id === file.id && !beat.end_time) {
            return {
              ...beat,
              start_time: beat.start_time || 0,
              end_time: currentTime,
            }
          }
          return beat
        }
      )
    },
    [],
  )

  const onAction = React.useCallback(
    (key, item, pressed = false) => {
      setActions(_actions => {
        if (pressed) {
          if (!_.includes(keys.current, key)) {
            keys.current = [...keys.current, key]
          }

        } else {
          keys.current = _.filter(keys.current, i => i !== key)

        }

        let currentAudios = _.get(_actions, 'audios', [])
        let currentFiles = _.get(_actions, 'files', [])
        let currentFile = _.get(_actions, 'file', null)
        let countSameFile = _.get(_actions, 'countSameFile', 0)

        if (! item.file?.id && ! item.audio?.id) return
        if (recRef.current) {
          startRec.current = true
        }
        const currentTime = startRec.current ? _.parseInt(time.current, 10) : null

        if (pressed) {
          if (item.file?.id && ! _.find(currentFiles, i => i.id === item.file.id)) {
            addBeat(item.file, currentTime)
            currentFiles.push(item.file)
            countSameFile = currentFile?.id === item.file.id ? countSameFile + 1 : 0
            currentFile = item.file
          }

          if (item.audio?.id && ! _.find(currentAudios, i => i.id === item.audio.id)) {
            addBeat(item.audio, currentTime)
            currentAudios.push(item.audio)
          }

        } else {

          if (item.file?.id &&_.find(currentFiles, i => i.id === item.file.id)) {
            removeBeat(item.file, currentTime)
            currentFiles = _.filter(currentFiles, i => i.id !== item.file.id)
          }

          if (item.audio?.id && _.find(currentAudios, i => i.id === item.audio.id)) {
            removeBeat(item.audio, currentTime)
            currentAudios = _.filter(currentAudios, i => i.id !== item.audio.id)
          }
        }

        return {
          file: currentFile,
          countSameFile,
          audios: currentAudios,
          files: currentFiles,
        }
      })
    },
    [setActions] // eslint-disable-line
  )

  const onSubmitComposition = e => {
    e.preventDefault()

    const title = e.currentTarget.elements[0].value
    const description = e.currentTarget.elements[1].value
    const privacy = e.currentTarget.elements[2].checked ? 'public' : 'private'

    const createComposition = async (title, description, privacy, beats) => {
      try {
        const response = await addComposition({
          beats: beats.current,
          duration: totalDuration.current,
          title,
          description,
          privacy,
        })
        if (response?.error) {
          throw response?.error
        }
      } catch(e) {
        alert('Please, complete all fields and submit the form again')
        return
      }
      history.push('/account')
    }
    if (totalDuration.current > 0) {
      createComposition(title, description, privacy, beats)
    }
  }

  return (
    <Authenticated>
      <div className='flex flex-col w-full h-screen overflow-hidden' style={{'touchAction': 'none'}}>
        <Modal active={rec === false} onClose={() => setRec(null)}>
          <Content className='prose-sm lg:prose-lg'>
            <h3>Export video</h3>
            <form onSubmit={onSubmitComposition}>
              <Input
                disabled={isAddingComposition}
                inputProps={{
                  placeholder: 'Title',
                }}
              />
              <Textarea
                inputProps={{
                  disabled: isAddingComposition,
                  placeholder: 'Description',
                }}
              />
              <InputCheckbox containerClassName='mb-5 mt-5' disabled={isAddingComposition} placeholder='Public composition'/>
              <Button type='submit' className='btn invert' loading={isAddingComposition}>Save</Button>
            </form>
          </Content>
        </Modal>
        <div
          onClick={() => {history.goBack()}}
          className='
            fixed text-lg lg:text-2xl left-0 top-0 z-30
            p-3
            flex items-center space-x-2
            transition-all
            shadow-lg
            cursor-pointer
            bg-white rounded-br-xl
            text-disabled-700 hover:text-primary-600
          '
        >
          <FaTimes />
        </div>
        <LayoutPlayer
          time={time.current}
          actions={actions}
          setRec={setRec}
          rec={rec}
        />
        <div className='flex-grow overflow-auto'>
          <div className='grid grid-cols-5 md:grid-cols-7 lg:grid-cols-13 gap-1 p-1'>
            {
              _.map(
                items,
                (item, index) => {
                  const filesAreLoaded = (
                    (
                      item.file &&
                      item.audio &&
                      item.file?.error === false &&
                      item.audio?.error === false
                    ) || (
                      item.file &&
                      item.file?.error === false
                    ) || (
                      item.audio &&
                      item.audio?.error === false
                    )
                  )

                  return (
                    <div
                      onTouchStart={() => onAction(letters[index], item, true)}
                      onTouchEnd={() => onAction(letters[index], item, false)}
                      className='relative select-none bg-disabled-300 composition-item'
                      key={`thum_${item.id}`}
                    >
                      {
                        filesAreLoaded &&
                        <>
                          {
                            rec !== false &&
                            <KeyEvent
                              allowedKeys={[letters[index]]}
                              onKeyDown={() => onAction(letters[index], item, true)}
                              onKeyUp={() => onAction(letters[index], item, false)}
                            />
                          }
                          <AlbumItemThumb
                            key={`item_${item.id}`}
                            file={item.file}
                            className='absolute'
                            overlay={
                              <div className='
                                absolute w-100 h-100
                                items-center justify-center
                                hidden lg:flex z-10
                              '>
                                <div
                                  className={
                                    classNames([
                                      'bg-black shadow-lg bg-opacity-80 rounded-full',
                                      'text-white text-2xl font-bold',
                                      'flex items-center justify-center',
                                      'w-10 h-10',
                                      'transition duration-75',
                                      {
                                        'bg-primary-600': _.includes(keys.current, letters[index]),
                                      }
                                    ])
                                  }
                                >
                                  {_.get(letters, index, null)}<br/>
                                </div>
                              </div>
                            }
                          />
                        </>
                      }
                      <Loader
                        className='
                          w-10 h-10 bg-white rounded-full bg-opacity-80 p-2
                          text-black absolute z-10 left-1/2 top-1/2
                          transform -translate-x-1/2 -translate-y-1/2
                        '
                        active={ ! filesAreLoaded}
                      />
                    </div>
                  )
                }
              )
            }
          </div>
        </div>
      </div>
    </Authenticated>
  )
}
